import React, { useState, useEffect } from 'react';
import {
  Modal,
  Checkbox,
  Button,
  InlineNotification,
  Toggle,
  Accordion,
  AccordionItem,
  Tag,
  Stack,
  Grid,
  Column,
  DatePicker,
  DatePickerInput,
  TextInput,
  RadioButtonGroup,
  RadioButton,
  ProgressIndicator,
  ProgressStep,
  Tooltip,
  Link,
} from '@carbon/react';
import {
  CheckmarkFilled,
  WarningAltFilled,
  InformationFilled,
  Time,
  UserAvatar,
  Document,
  Security,
} from '@carbon/icons-react';
import { privacyService, ConsentRecord } from '../../services/privacyService';
import { useAuth } from '../../hooks/useAuth';

interface ConsentCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
  dataTypes: string[];
  purposes: string[];
  retention: string;
  thirdParties?: string[];
  withdrawable: boolean;
}

interface ConsentStatus {
  category: ConsentCategory;
  consented: boolean;
  consentedAt?: string;
  expiresAt?: string;
  parentConsent?: boolean;
  parentConsentedAt?: string;
}

const consentCategories: ConsentCategory[] = [
  {
    id: 'essential',
    name: 'Essential Services',
    description: 'Required for the platform to function properly',
    required: true,
    dataTypes: ['Account information', 'Authentication data', 'Security logs'],
    purposes: ['Service provision', 'Security', 'Legal compliance'],
    retention: 'Account duration + 1 year',
    withdrawable: false,
  },
  {
    id: 'educational',
    name: 'Educational Records',
    description: 'Academic progress and learning data (FERPA protected)',
    required: false,
    dataTypes: ['Grades', 'Assignments', 'Progress tracking', 'Certifications'],
    purposes: ['Educational services', 'Progress reporting', 'Certification'],
    retention: '5 years (FERPA requirement)',
    withdrawable: true,
  },
  {
    id: 'analytics',
    name: 'Analytics & Performance',
    description: 'Help us improve the platform experience',
    required: false,
    dataTypes: ['Usage patterns', 'Performance metrics', 'Error logs'],
    purposes: ['Service improvement', 'Performance optimization'],
    retention: '2 years',
    withdrawable: true,
  },
  {
    id: 'personalization',
    name: 'Personalized Learning',
    description: 'Customize content and recommendations',
    required: false,
    dataTypes: ['Learning preferences', 'Interaction history', 'Content preferences'],
    purposes: ['Personalization', 'Recommendations', 'Adaptive learning'],
    retention: 'Until withdrawn',
    withdrawable: true,
  },
  {
    id: 'communication',
    name: 'Communication Preferences',
    description: 'Updates and notifications about your learning',
    required: false,
    dataTypes: ['Contact information', 'Communication preferences'],
    purposes: ['Service updates', 'Educational notifications', 'Support'],
    retention: 'Until withdrawn',
    withdrawable: true,
  },
  {
    id: 'marketing',
    name: 'Marketing Communications',
    description: 'Promotional content and offers',
    required: false,
    dataTypes: ['Contact information', 'Interests'],
    purposes: ['Marketing', 'Promotions', 'Surveys'],
    retention: 'Until withdrawn',
    withdrawable: true,
  },
  {
    id: 'thirdparty',
    name: 'Third-Party Integrations',
    description: 'Share data with educational partners',
    required: false,
    dataTypes: ['Profile data', 'Progress data'],
    purposes: ['Integration services', 'Extended features'],
    retention: 'Until withdrawn',
    thirdParties: ['Learning Management Systems', 'Assessment Providers'],
    withdrawable: true,
  },
];

export const ConsentManagementInterface: React.FC = () => {
  const { user } = useAuth();
  const [consentStatuses, setConsentStatuses] = useState<ConsentStatus[]>([]);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ConsentCategory | null>(null);
  const [parentalConsentRequired, setParentalConsentRequired] = useState(false);
  const [parentEmail, setParentEmail] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<'email' | 'creditcard' | 'id'>('email');
  const [consentProgress, setConsentProgress] = useState(0);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    subtitle?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConsentData();
    checkMinorStatus();
  }, [user]);

  const loadConsentData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const consents = await privacyService.getUserConsents(user.id);

      const statuses = consentCategories.map(category => {
        const existingConsent = consents.find(c => c.type === category.id);
        return {
          category,
          consented: existingConsent?.granted || category.required,
          consentedAt: existingConsent?.timestamp,
          expiresAt: existingConsent?.expiresAt,
          parentConsent: existingConsent?.parentConsentRequired,
          parentConsentedAt: existingConsent?.parentConsentId ? existingConsent.timestamp : undefined,
        };
      });

      setConsentStatuses(statuses);
      calculateConsentProgress(statuses);
    } catch (error) {
      console.error('Failed to load consent data:', error);
      setNotification({
        type: 'error',
        title: 'Failed to load consent preferences',
        subtitle: 'Please try refreshing the page',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkMinorStatus = async () => {
    if (!user) return;

    try {
      const { isMinor, requiresParentalConsent } = await privacyService.checkMinorStatus(user.id);
      setParentalConsentRequired(requiresParentalConsent);

      if (requiresParentalConsent) {
        setNotification({
          type: 'warning',
          title: 'Parental consent required',
          subtitle: 'Some features require parental consent for users under 13 (COPPA)',
        });
      }
    } catch (error) {
      console.error('Failed to check minor status:', error);
    }
  };

  const calculateConsentProgress = (statuses: ConsentStatus[]) => {
    const total = statuses.filter(s => !s.category.required).length;
    const consented = statuses.filter(s => !s.category.required && s.consented).length;
    setConsentProgress(total > 0 ? (consented / total) * 100 : 0);
  };

  const handleConsentToggle = async (category: ConsentCategory, granted: boolean) => {
    if (!user) return;

    if (parentalConsentRequired && !category.required) {
      setSelectedCategory(category);
      setShowConsentModal(true);
      return;
    }

    try {
      await privacyService.updateConsent(user.id, category.id, granted, {
        category: category.name,
        purposes: category.purposes,
        dataTypes: category.dataTypes,
      });

      setNotification({
        type: 'success',
        title: `Consent ${granted ? 'granted' : 'withdrawn'} successfully`,
        subtitle: `Your preference for ${category.name} has been updated`,
      });

      await loadConsentData();
    } catch (error) {
      console.error('Failed to update consent:', error);
      setNotification({
        type: 'error',
        title: 'Failed to update consent',
        subtitle: 'Please try again',
      });
    }
  };

  const handleParentalConsentRequest = async () => {
    if (!user || !selectedCategory || !parentEmail) return;

    try {
      await privacyService.requestParentalConsent(user.id, parentEmail, [selectedCategory.id]);

      setNotification({
        type: 'success',
        title: 'Parental consent request sent',
        subtitle: `An email has been sent to ${parentEmail} for verification`,
      });

      setShowConsentModal(false);
      setParentEmail('');
      setSelectedCategory(null);
    } catch (error) {
      console.error('Failed to request parental consent:', error);
      setNotification({
        type: 'error',
        title: 'Failed to send consent request',
        subtitle: 'Please check the email address and try again',
      });
    }
  };

  const handleWithdrawAllConsent = async () => {
    if (!user) return;

    const withdrawable = consentStatuses.filter(s => s.category.withdrawable && s.consented);

    if (withdrawable.length === 0) {
      setNotification({
        type: 'info',
        title: 'No withdrawable consents',
        subtitle: 'You have no active consents that can be withdrawn',
      });
      return;
    }

    try {
      await Promise.all(
        withdrawable.map(status =>
          privacyService.withdrawConsent(user.id, status.category.id, 'User requested withdrawal')
        )
      );

      setNotification({
        type: 'success',
        title: 'All consents withdrawn',
        subtitle: 'Your withdrawable consent preferences have been updated',
      });

      await loadConsentData();
    } catch (error) {
      console.error('Failed to withdraw consents:', error);
      setNotification({
        type: 'error',
        title: 'Failed to withdraw consents',
        subtitle: 'Some consents could not be withdrawn',
      });
    }
  };

  const getConsentIcon = (status: ConsentStatus) => {
    if (status.category.required) return <Security size={16} />;
    if (status.consented) return <CheckmarkFilled size={16} />;
    if (status.parentConsent) return <UserAvatar size={16} />;
    return <InformationFilled size={16} />;
  };

  const getConsentColor = (status: ConsentStatus) => {
    if (status.category.required) return 'blue';
    if (status.consented) return 'green';
    return 'gray';
  };

  return (
    <div className="consent-management-interface">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <Stack gap={6}>
            {/* Header */}
            <div className="consent-header">
              <h2>Consent Management</h2>
              <p>Control how your data is collected and used. Required consents cannot be withdrawn.</p>

              {parentalConsentRequired && (
                <InlineNotification
                  kind="warning"
                  title="Parental consent required"
                  subtitle="As a user under 13, parental consent is required for certain data processing activities (COPPA compliance)"
                  lowContrast
                  hideCloseButton
                />
              )}
            </div>

            {/* Consent Progress */}
            <div className="consent-progress">
              <h3>Your Privacy Choices</h3>
              <ProgressIndicator currentIndex={Math.floor(consentProgress / 20)} spaceEqually>
                <ProgressStep label="Essential" description="Required" />
                <ProgressStep label="Educational" />
                <ProgressStep label="Analytics" />
                <ProgressStep label="Personalization" />
                <ProgressStep label="Complete" />
              </ProgressIndicator>
              <p className="progress-text">{Math.round(consentProgress)}% of optional consents configured</p>
            </div>

            {/* Notifications */}
            {notification && (
              <InlineNotification
                kind={notification.type}
                title={notification.title}
                subtitle={notification.subtitle}
                onClose={() => setNotification(null)}
                lowContrast
              />
            )}

            {/* Consent Categories */}
            <Accordion>
              {consentStatuses.map(status => (
                <AccordionItem
                  key={status.category.id}
                  title={
                    <div className="consent-accordion-title">
                      <div className="consent-title-content">
                        {getConsentIcon(status)}
                        <span>{status.category.name}</span>
                        {status.category.required && (
                          <Tag type="blue" size="sm">
                            Required
                          </Tag>
                        )}
                        {status.parentConsent && (
                          <Tag type="cyan" size="sm">
                            Parent Consent
                          </Tag>
                        )}
                      </div>
                      <Tag type={getConsentColor(status)} size="sm">
                        {status.consented ? 'Consented' : 'Not Consented'}
                      </Tag>
                    </div>
                  }
                >
                  <div className="consent-details">
                    <p className="consent-description" id={`consent-${status.category.id}-description`}>
                      {status.category.description}
                    </p>

                    <Grid narrow>
                      <Column lg={8} md={4} sm={4}>
                        <div className="consent-info-section">
                          <h4>Data Types</h4>
                          <ul>
                            {status.category.dataTypes.map(type => (
                              <li key={type}>{type}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="consent-info-section">
                          <h4>Purposes</h4>
                          <ul>
                            {status.category.purposes.map(purpose => (
                              <li key={purpose}>{purpose}</li>
                            ))}
                          </ul>
                        </div>
                      </Column>

                      <Column lg={8} md={4} sm={4}>
                        <div className="consent-info-section">
                          <h4>Data Retention</h4>
                          <p>{status.category.retention}</p>
                        </div>

                        {status.category.thirdParties && (
                          <div className="consent-info-section">
                            <h4>Third Parties</h4>
                            <ul>
                              {status.category.thirdParties.map(party => (
                                <li key={party}>{party}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {status.consentedAt && (
                          <div className="consent-info-section">
                            <h4>Consent History</h4>
                            <p>Granted: {new Date(status.consentedAt).toLocaleDateString()}</p>
                            {status.expiresAt && <p>Expires: {new Date(status.expiresAt).toLocaleDateString()}</p>}
                          </div>
                        )}
                      </Column>
                    </Grid>

                    {!status.category.required && (
                      <div className="consent-actions">
                        <Toggle
                          id={`consent-${status.category.id}`}
                          labelText={status.consented ? 'Withdraw consent' : 'Grant consent'}
                          toggled={status.consented}
                          onToggle={value => handleConsentToggle(status.category, value)}
                          disabled={loading}
                          aria-label={`${status.consented ? 'Withdraw' : 'Grant'} consent for ${status.category.name} - ${status.category.description}`}
                          aria-describedby={`consent-${status.category.id}-description`}
                        />
                        {status.category.withdrawable && status.consented && (
                          <p className="consent-note">
                            <InformationFilled size={16} />
                            You can withdraw this consent at any time
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Actions */}
            <div className="consent-actions-footer">
              <Button kind="secondary" renderIcon={Document} href="/privacy-policy" target="_blank">
                View Privacy Policy
              </Button>
              <Button
                kind="secondary"
                renderIcon={Time}
                onClick={() => (window.location.href = '/privacy/consent-history')}
              >
                View Consent History
              </Button>
              <Button kind="danger--tertiary" onClick={handleWithdrawAllConsent} disabled={loading}>
                Withdraw All Optional Consents
              </Button>
            </div>
          </Stack>
        </Column>
      </Grid>

      {/* Parental Consent Modal */}
      <Modal
        open={showConsentModal}
        modalHeading="Parental Consent Required"
        modalLabel="COPPA Compliance"
        primaryButtonText="Send Request"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleParentalConsentRequest}
        onRequestClose={() => {
          setShowConsentModal(false);
          setSelectedCategory(null);
          setParentEmail('');
        }}
      >
        <Stack gap={5}>
          <InlineNotification
            kind="info"
            title="Why is this required?"
            subtitle="Users under 13 require verifiable parental consent for data processing under COPPA regulations"
            hideCloseButton
            lowContrast
          />

          {selectedCategory && (
            <div>
              <h4>Requesting consent for: {selectedCategory.name}</h4>
              <p>{selectedCategory.description}</p>
            </div>
          )}

          <TextInput
            id="parent-email"
            labelText="Parent or Guardian Email"
            placeholder="parent@example.com"
            value={parentEmail}
            onChange={e => setParentEmail(e.target.value)}
            required
          />

          <RadioButtonGroup
            legendText="Verification Method"
            name="verification-method"
            defaultSelected="email"
            onChange={value => setVerificationMethod(value as any)}
          >
            <RadioButton id="verify-email" labelText="Email verification" value="email" />
            <RadioButton
              id="verify-creditcard"
              labelText="Credit card verification ($0.50 charge)"
              value="creditcard"
            />
            <RadioButton id="verify-id" labelText="Government ID verification" value="id" />
          </RadioButtonGroup>

          <p className="consent-modal-note">
            An email will be sent to your parent or guardian with instructions to verify and grant consent. This process
            typically takes 24-48 hours.
          </p>
        </Stack>
      </Modal>
    </div>
  );
};
